/**
 * We use a custom route at /s?q= to perform the search. This component
 * redirects the user to /s?q={term} when the user either clicks on the
 * button or submits the form. Make sure this page exists in deco.cx/admin
 * of yout site. If not, create a new page on this route and add the appropriate
 * loader.
 *
 * Note that this is the most performatic way to perform a search, since
 * no JavaScript is shipped to the browser!
 */

import { TargetedEvent, useEffect, useRef } from "preact/compat";
import Icon from "$store/components/ui/Icon.tsx";
import Spinner from "$store/components/ui/Spinner.tsx";
import { useAutocomplete } from "$store/sdk/useAutocomplete.tsx";
import { useUI } from "$store/sdk/useUI.ts";
import { sendEvent } from "$store/sdk/analytics.tsx";
import { formatPrice } from "$store/sdk/format.ts";
import { useOffer } from "$store/sdk/useOffer.ts";
// import { SendEventOnClick } from "$store/sdk/analytics.tsx";
// import { mapProductToAnalyticsItem } from "deco-sites/std/commerce/utils/productToAnalyticsItem.ts";
import Image from "apps/website/components/Image.tsx";
import type { Product, Search } from "apps/commerce/types.ts";

export interface Props {
  /**
   * @title Placeholder
   * @description Search bar default placeholder message
   * @default Procure por produtos e marcas...
   */
  placeholder?: string;
  /**
   * @title Page path
   * @description When user clicks on the search button, navigate it to
   * @default /s
   */
  action?: string;
  /**
   * @title Term name
   * @description Querystring param used when navigating the user
   * @default q
   */
  name?: string;
  /**
   * TODO: Receive querystring from parameter in the server-side
   */
  query?: string;
}

function Searchbar({
  placeholder = "Procure por produtos e marcas...",
  action = "/s",
  name = "q",
  query,
}: Props) {
  const { displaySearchbar } = useUI();
  const searchRef = useRef<HTMLDivElement>(null);
  const term = searchRef.current?.querySelector("input")?.value;
  const { suggestions, loading } = useAutocomplete();
  const hasProducts = Boolean(suggestions.value?.products?.length);
  const hasTerms = Boolean(suggestions.value?.searches?.length);
  const open = displaySearchbar.value && term && (hasProducts || hasTerms);

  useEffect(() => {
    const closeWithOutsideClick = (e: Event) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        displaySearchbar.value = false;
      }
    };

    if (open) document?.addEventListener("click", closeWithOutsideClick);

    return () => document?.removeEventListener("click", closeWithOutsideClick);
  }, [open]);

  return (
    <div
      class={`mx-2 border border-tw-primary-100 rounded-3xl relative lg:w-full lg:max-w-sm lg:mx-0 lg:justify-self-end lg:group-data-[micro-header=true]/header:col-start-4 lg:group-data-[micro-header=true]/header:col-span-1 ${
        open ? "rounded-b-none" : ""
      }`}
      ref={searchRef}
    >
      <Form
        action={action}
        name={name}
        placeholder={placeholder}
        query={query}
      />

      {open && (
        <div class="px-3 pb-6 border border-tw-primary-100 border-t-0 rounded-b-xl bg-white absolute -right-px -left-px z-20">
          <Terms
            terms={suggestions.value?.searches}
            action={action}
            loading={loading}
          />

          <Products
            products={suggestions.value?.products}
            action={action}
            term={term}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
}

function Form(
  { action, name, placeholder, query }: {
    action: string;
    name?: string;
    placeholder?: string;
    query?: string;
  },
) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { displaySearchbar } = useUI();
  const { setSearch, suggestions } = useAutocomplete();

  const onInput = (e: TargetedEvent<HTMLInputElement, Event>) => {
    const value = e.currentTarget.value;

    if (value) {
      displaySearchbar.value = true;

      sendEvent({
        name: "search",
        params: { search_term: value },
      });
    }

    setSearch(value);
  };

  const onFocus = () => {
    const hasProducts = Boolean(suggestions.value?.products?.length);
    const hasTerms = Boolean(suggestions.value?.searches?.length);

    if (!displaySearchbar.value && (hasTerms || hasProducts)) {
      displaySearchbar.value = true;
    }
  };

  const startRecording = (e: Event) => {
    e.preventDefault();

    if ("webkitSpeechRecognition" in self) {
      // deno-lint-ignore no-explicit-any
      const recognition = new (self.webkitSpeechRecognition as any)();
      recognition.lang = "pt-BR";

      // deno-lint-ignore no-explicit-any
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript.replace(
          /[^\w\s]/gi,
          "",
        );

        const inputSearch = searchInputRef.current;

        if (inputSearch) {
          inputSearch.value = transcript ?? " ";
          const changeEvent = new Event("change", { bubbles: true });
          inputSearch.dispatchEvent(changeEvent);
          self.location.href = `/s?q=${transcript}`;
        }
      };

      recognition.start();

      setTimeout(() => {
        recognition.stop();
      }, 5000);
    }
  };

  return (
    <form id="searchbar" class="h-12 flex items-center" action={action}>
      <button
        class="w-12 h-12 rounded-full flex justify-center items-center shrink-0"
        type="button"
        onClick={startRecording}
        title="Pesquisa por voz"
        aria-label="Pesquisa por voz"
      >
        <Icon
          id="Microphone"
          class="text-tw-primary-100"
          width={17}
          height={25}
        />
      </button>

      <input
        id="search-input"
        class="w-full h-full outline-none text-sm placeholder:text-tw-neutral-300"
        ref={searchInputRef}
        name={name}
        placeholder={placeholder}
        type="text"
        defaultValue={query}
        autocomplete="off"
        onInput={onInput}
        onFocus={onFocus}
      />

      <button
        class="w-12 h-12 rounded-full bg-tw-base-50 flex justify-center items-center shrink-0"
        title="Pesquisar"
        aria-label="Pesquisar"
      >
        <Icon
          id="MagnifyingGlass"
          class="text-tw-primary-100"
          width={24}
          height={24}
        />
      </button>
    </form>
  );
}

function Terms({ terms, action, loading }: {
  terms?: Search[];
  action: string;
  loading: { value: boolean };
}) {
  if (!terms?.length) return null;

  return (
    <div class="pt-3 mt-2 border-t border-tw-neutral-100">
      <div class="flex items-center gap-1">
        <span class="text-tw-primary-200 text-sm font-maven font-extrabold">
          Sugestões
        </span>

        {loading.value && <Spinner class="text-tw-primary-100" size={12} />}
      </div>

      <ul class="mt-2">
        {terms.map(({ term }) => (
          <li>
            <a href={`${action}?q=${term}`}>
              {term}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Products({ products, action, term, loading }: {
  products?: Product[];
  action?: string;
  term?: string;
  loading: { value: boolean };
}) {
  if (!products?.length) return null;

  return (
    <div class="pt-3 mt-2 border-t border-tw-neutral-100">
      <div class="flex items-center gap-1">
        <span class="text-tw-primary-200 text-sm font-maven font-extrabold">
          Produtos
        </span>

        {loading.value && <Spinner class="text-tw-primary-100" size={12} />}
      </div>

      <ul class="max-h-[9.25rem] mt-2 overflow-auto grid gap-2 scrollbar">
        {products.map((
          product,
        ) => {
          const { productID, image, name, isVariantOf, offers, url } = product;
          const id = `product-card-${productID}`;
          const { listPrice = 0, price = 0 } = useOffer(offers);

          return (
            <li>
              {
                /* <SendEventOnClick
                id={id}
                event={{
                  name: "select_item" as const,
                  params: {
                    items: [
                      mapProductToAnalyticsItem({
                        product,
                        price,
                        listPrice,
                      }),
                    ],
                  },
                }}
              /> */
              }

              <a class="flex items-center gap-3" href={url}>
                <Image
                  class="border border-tw-neutral-100 rounded"
                  src={image?.[0].url!}
                  alt={image?.[0].alternateName}
                  width={44}
                  height={44}
                  loading="lazy"
                  fetchPriority="low"
                />

                <div>
                  <strong class="text-tw-neutral-500 text-xs font-bold line-clamp-1">
                    {product.isVariantOf?.name} {name}
                  </strong>

                  {listPrice && listPrice > price && (
                    <span class="mr-2 text-tw-neutral-400 text-xs line-through inline-block">
                      De: {formatPrice(listPrice)}
                    </span>
                  )}

                  {price && (
                    <span class="text-tw-secondary-200 text-sm font-extrabold">
                      {listPrice && listPrice > price && "Por:"}{" "}
                      {formatPrice(price)}
                    </span>
                  )}
                </div>
              </a>
            </li>
          );
        })}
      </ul>

      {products.length > 1 && (
        <a
          class="mt-4 text-tw-neutral-500 text-sm text-center font-maven font-semibold underline block"
          href={`${action}?q=${term}`}
        >
          Ver todos {products.length} produtos
        </a>
      )}
    </div>
  );
}

export default Searchbar;
